import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Card,
  Skeleton,
  Breadcrumbs,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

// Services & Web3
import cinstance, {
  ca as contractAddress,
} from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";
import {
  LOTTERY_TYPES,
  STATUS_CONFIG,
  lotteryTypeMapper,
  resultStatusTypeMapper,
  Path,
  getNetworkCurrency,
  DEFAULT_CURRENCY,
} from "../../../../static/Constants";
import { networkID } from "../../../../static/utils";
import getImageUrlByHash from "../../../../static/logoUtils";
import { CommonButton } from "../../../Components/ui";

// Subcomponents
import LotteryHeroCard from "./components/LotteryHeroCard";
import LotteryWinnerCard from "./components/LotteryWinnerCard";
import LotterySpecsCard from "./components/LotterySpecsCard";
import LotteryScheduleCard from "./components/LotteryScheduleCard";
import LotteryPartnerCard from "./components/LotteryPartnerCard";
import LotterySecurityNotice from "./components/LotterySecurityNotice";

import "./LotteryDetails.css";

const NoWinner = "0x0000000000000000000000000000000000000000";

const withTimeout = (promise, ms = 5000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms),
    ),
  ]);
};

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const UserLotteryDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [networkId, setNetworkId] = useState(networkID);
  const [lottery, setLottery] = useState(null);
  const [datelist, setDateList] = useState(null);
  const [winnersEvent, setWinnersEvent] = useState([]);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  const apiUrl = process.env.REACT_APP_API_KEY;

  /**
   * Fetch On-Chain Lottery Data with Timeout & Concurrency Protection
   */
  const fetchLotteryData = useCallback(
    async (isRefresh = false) => {
      if (!id) {
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Safety watchdog to guarantee loading state is never permanently stuck
      let watchdogTimer = null;
      watchdogTimer = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7000);

      try {
        if (window.ethereum) {
          window.web3 = web3;
          if (window.ethereum.networkVersion) {
            setNetworkId(window.ethereum.networkVersion);
          } else if (web3?.eth?.net?.getId) {
            withTimeout(web3.eth.net.getId(), 2000)
              .then((netId) => {
                if (netId) setNetworkId(netId);
              })
              .catch(() => {});
          }
        }

        // 1. Fetch Main Lottery & Schedule concurrently with timeout protection
        const [lotteryRes, datesRes] = await Promise.all([
          withTimeout(cinstance.methods.lottery(id).call(), 5000).catch(
            (err) => {
              console.warn("Error or timeout fetching lottery details:", err);
              return null;
            },
          ),
          withTimeout(cinstance.methods.lotteryDates(id).call(), 5000).catch(
            (err) => {
              console.warn("Error or timeout fetching lottery dates:", err);
              return null;
            },
          ),
        ]);

        if (
          !lotteryRes ||
          !lotteryRes.lotteryId ||
          lotteryRes.lotteryId === "0"
        ) {
          setLottery(null);
          setDateList(null);
          return;
        }

        setLottery(lotteryRes);
        if (datesRes) {
          setDateList(datesRes);
        }

        // 2. Fetch Winner Events asynchronously (non-blocking)
        try {
          withTimeout(
            cinstance.getPastEvents("LotteryResult", {
              filter: { lotteryId: `${id}` },
              fromBlock: 0,
              toBlock: "latest",
            }),
            5000,
          )
            .then((events) => {
              if (events && events.length > 0) {
                setWinnersEvent(events);
              }
            })
            .catch((evtErr) => {
              console.warn("Could not fetch LotteryResult events:", evtErr);
            });
        } catch (evtErr) {
          console.warn("Could not initiate getPastEvents:", evtErr);
        }

        // 3. Fetch Partner Details asynchronously (non-blocking)
        const partnerAddr = lotteryRes.partner || lotteryRes.partnerAddress;
        if (partnerAddr && partnerAddr !== NoWinner) {
          withTimeout(cinstance.methods.partnerbyaddr(partnerAddr).call(), 4000)
            .then((pData) => {
              if (pData) {
                setPartnerDetails(pData);
              }
            })
            .catch((pErr) => {
              console.warn("Could not fetch partner details:", pErr);
            });
        }

        if (isRefresh) {
          toast.success("Lottery details refreshed!");
        }
      } catch (err) {
        console.error("Error fetching lottery details:", err);
        toast.error("Failed to load lottery data from blockchain.");
      } finally {
        if (watchdogTimer) {
          clearTimeout(watchdogTimer);
        }
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id],
  );

  // Load Partner Logos (Optional S3 service)
  useEffect(() => {
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          setPartnerLogoData(response?.data?.result || []);
        })
        .catch(() => {
          setPartnerLogoData([]);
        });
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchLotteryData();
  }, [fetchLotteryData]);

  // Derived Game Type and Status
  const typeId = lottery ? Number(lottery.lotteryType) : 0;
  const statusId = lottery ? Number(lottery.status) : 0;

  const gameTypeConfig = LOTTERY_TYPES[typeId] || {
    name: lotteryTypeMapper[typeId] || `Lottery Type ${typeId}`,
    shortName: "Lottery",
    color: colors.primary.main,
    bgColor: colors.blue[50],
    gradient: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  };

  const statusConfig = STATUS_CONFIG[statusId] || {
    label: resultStatusTypeMapper[statusId] || "Unknown",
    variant: "neutral",
  };

  const isOpen = statusId === 0 || statusId === 5;
  const hasWinner =
    lottery && lottery.lotteryWinner && lottery.lotteryWinner !== NoWinner;

  const winningNumber = useMemo(() => {
    if (!winnersEvent || winnersEvent.length === 0) return null;
    const evt = winnersEvent[0];
    if (evt?.returnValues?.n) return evt.returnValues.n;
    if (evt?.returnValues?.number) {
      return evt.returnValues.number
        .split("-")
        .slice(0, -1)
        .reverse()
        .join("-");
    }
    return null;
  }, [winnersEvent]);

  // Determine Game Play Link
  const playLink = useMemo(() => {
    return Path.getLotteryPlayUrl(id, typeId);
  }, [typeId, id]);

  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId],
  );

  const entryFeeInCurrency = useMemo(
    () => safeFromWei(lottery?.entryFee),
    [lottery?.entryFee],
  );

  const prizeInCurrency = useMemo(
    () => safeFromWei(lottery?.totalPrize),
    [lottery?.totalPrize],
  );

  const partnerLogoUrl = useMemo(() => {
    if (!partnerDetails?.logoHash) return null;
    return getImageUrlByHash(partnerDetails.logoHash, partnerLogoData);
  }, [partnerDetails?.logoHash, partnerLogoData]);

  return (
    <Box
      className="user-lottery-details-canvas"
      sx={{ backgroundColor: colors.dashboard.canvas }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <NavBar />

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              sx={{ color: colors.slate[400] }}
            />
          }
          sx={{ mb: 3 }}
        >
          <Link
            to={Path.root}
            style={{
              color: colors.slate[500],
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Home
          </Link>
          <Link
            to={Path.allLotteries}
            style={{
              color: colors.slate[500],
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            All Lotteries
          </Link>
          <Typography
            sx={{
              color: colors.primary.main,
              fontWeight: 700,
              fontSize: "0.875rem",
              textAlign: "left",
            }}
          >
            Lottery #{id}
          </Typography>
        </Breadcrumbs>

        {/* Back Button & Top Navigation Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <CommonButton
            variant="outline"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push(Path.allLotteries)}
            sx={{
              color: colors.slate[700],
              borderColor: colors.slate[300],
              bgcolor: "#ffffff",
              "&:hover": {
                borderColor: colors.slate[400],
                bgcolor: colors.slate[100],
                color: colors.slate[900],
              },
            }}
          >
            Back to Lotteries
          </CommonButton>

          {isOpen && !loading && lottery && (
            <CommonButton
              variant="playThisLottery"
              onClick={() => history.push(playLink)}
              sx={{
                ...(gameTypeConfig.gradient
                  ? { background: gameTypeConfig.gradient }
                  : {}),
              }}
            >
              Play This Lottery
            </CommonButton>
          )}
        </Stack>

        {/* Loading State Skeletons */}
        {loading && (
          <Stack spacing={3}>
            <Skeleton
              variant="rounded"
              height={180}
              animation="wave"
              sx={{
                bgcolor: colors.slate[200],
                borderRadius: borderRadiusPx.xl,
              }}
            />
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Skeleton
                    variant="rounded"
                    height={260}
                    animation="wave"
                    sx={{
                      bgcolor: colors.slate[200],
                      borderRadius: borderRadiusPx.xl,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Skeleton
                    variant="rounded"
                    height={260}
                    animation="wave"
                    sx={{
                      bgcolor: colors.slate[200],
                      borderRadius: borderRadiusPx.xl,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            {/* <Skeleton
              variant="rounded"
              height={100}
              animation="wave"
              sx={{
                bgcolor: colors.slate[200],
                borderRadius: borderRadiusPx.xl,
              }}
            /> */}
          </Stack>
        )}

        {/* Not Found State */}
        {!loading && !lottery && (
          <Card
            className="lottery-glass-card"
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: borderRadiusPx.xl,
              backgroundColor: "#ffffff",
              border: `1px solid ${colors.slate[200]}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: colors.slate[900], mb: 1.5 }}
            >
              Lottery #{id} Not Found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.slate[600],
                mb: 3,
                maxWidth: 450,
                mx: "auto",
              }}
            >
              The requested lottery could not be located on the smart contract.
              It may have expired or not exist.
            </Typography>
            <CommonButton
              variant="primary"
              onClick={() => history.push(Path.allLotteries)}
              sx={{ px: 4 }}
            >
              Browse All Lotteries
            </CommonButton>
          </Card>
        )}

        {/* Loaded Content */}
        {!loading && lottery && (
          <Stack spacing={3.5}>
            {/* 1. Hero / Header Card */}
            <LotteryHeroCard
              id={id}
              lottery={lottery}
              gameTypeConfig={gameTypeConfig}
              statusConfig={statusConfig}
              currency={currency}
              prizeInCurrency={prizeInCurrency}
              isOpen={isOpen}
              playLink={playLink}
              onRefresh={() => fetchLotteryData(true)}
              refreshing={refreshing}
            />

            {/* 2. Winner Announcement Card (if resolved) */}
            {hasWinner && (
              <LotteryWinnerCard
                winnerAddress={lottery.lotteryWinner}
                winningNumber={winningNumber}
                totalPrizeInCurrency={prizeInCurrency}
                currency={currency}
              />
            )}

            {/* 3. Specs & Schedule Grid */}
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <LotterySpecsCard
                    lottery={lottery}
                    entryFeeInCurrency={entryFeeInCurrency}
                    currency={currency}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LotteryScheduleCard
                    lottery={lottery}
                    datelist={datelist}
                    networkId={networkId}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* 4. Partner Co-host Card (if exists) */}
            {partnerDetails && (
              <LotteryPartnerCard
                partner={partnerDetails}
                partnerLogoUrl={partnerLogoUrl}
              />
            )}

            {/* 5. Security & Smart Contract Transparency Notice */}
            {/* <LotterySecurityNotice contractAddress={contractAddress} /> */}
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default UserLotteryDetails;
